<script setup>
defineOptions({ name: "PromocaoModulePage" });
import { defineAsyncComponent } from "vue";
import {
  TagIcon,
  Square3Stack3DIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  BuildingStorefrontIcon,
  ShoppingBagIcon,
  HeartIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-promocao.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "Você cria a campanha. O Kali Gestão aplica a regra. | Kali Sistemas",
  description:
    "Programe promoções por produto, quantidade e período e deixe o Kali Gestão identificar automaticamente quando a condição promocional deve ser aplicada.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional Promoção, descritas no material do produto.
const capacidades = [
  {
    icon: TagIcon,
    titulo: "Produtos",
    texto: "Defina quais itens participam da campanha.",
  },
  {
    icon: Square3Stack3DIcon,
    titulo: "Quantidade",
    texto: "Determine a partir de quando a condição promocional é ativada.",
  },
  {
    icon: CalendarDaysIcon,
    titulo: "Período",
    texto: "Escolha quando a promoção começa e termina.",
  },
  {
    icon: CheckCircleIcon,
    titulo: "Aplicação",
    texto: "Defina onde a campanha pode ser utilizada.",
  },
];

const steps = [
  {
    title: "Campanha é criada",
    description: "A promoção recebe uma identificação e um período.",
  },
  {
    title: "Produtos são definidos",
    description: "Os itens participantes são selecionados.",
  },
  {
    title: "Regra é configurada",
    description: "Quantidade mínima, preço promocional e demais condições são estabelecidos.",
  },
  {
    title: "Venda acontece",
    description: "O operador trabalha normalmente, sem cálculo manual.",
  },
  {
    title: "Promoção é aplicada",
    description: "Quando as condições são atendidas, o valor correspondente entra automaticamente na operação.",
  },
];

// Segmentos que se beneficiam de campanhas com regras automáticas.
const segmentos = [
  {
    icon: BuildingStorefrontIcon,
    titulo: "Supermercados e mercados",
    texto: "Ofertas por quantidade e campanhas semanais.",
  },
  {
    icon: ShoppingBagIcon,
    titulo: "Varejo",
    texto: "Campanhas sazonais e ações para produtos selecionados.",
  },
  {
    icon: HeartIcon,
    titulo: "Pet shops",
    texto: "Promoções de produtos, acessórios e serviços.",
  },
  {
    icon: TruckIcon,
    titulo: "Agropecuárias",
    texto: "Campanhas de produtos e insumos selecionados.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Material de construção",
    texto: "Ofertas para determinados itens ou quantidades.",
  },
  {
    icon: BriefcaseIcon,
    titulo: "Serviços",
    texto: "Campanhas de serviços em períodos determinados.",
  },
];

const faqItems = [
  {
    question: "Preciso alterar o preço do produto para fazer uma promoção?",
    answer:
      "Não. A proposta do recurso é manter o preço normal do produto e organizar as condições promocionais separadamente.",
  },
  {
    question: "Posso definir quando a promoção começa e termina?",
    answer:
      "Sim. A campanha pode possuir período de vigência.",
  },
  {
    question: "Posso criar promoção por quantidade?",
    answer:
      "Sim. A condição pode considerar uma quantidade mínima para aplicação do preço promocional.",
  },
  {
    question: "A promoção é aplicada automaticamente?",
    answer:
      "Quando a venda atende às regras definidas e a promoção está vigente, o sistema identifica e aplica a condição correspondente.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="Você cria a campanha. O Kali Gestão aplica a regra."
      description="Programe promoções por produto, quantidade e período e deixe o Kali Gestão identificar automaticamente quando a condição promocional deve ser aplicada. Menos descontos manuais, mais controle sobre suas campanhas."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Promoção não é apenas baixar preço. É definir uma regra comercial.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Uma promoção normalmente possui condições: pode valer apenas
            durante um período, somente para alguns produtos, a partir de
            determinada quantidade ou em algumas operações. O módulo de
            Promoção transforma essas condições em regras dentro do Kali
            Gestão.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Configure a condição. O sistema reconhece a oportunidade.
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            A regra fica no sistema, não na memória do operador.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Segmentos -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Promoções fazem parte de diferentes operações
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre Promoção" :items="faqItems" cta-label="Solicitar ativação" :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo Promoção. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
